import React, { useEffect, useState } from 'react';
import { adminGetSubscriptions, adminGetSubscription, adminGetClients } from '../../api';

const PLAN_STYLES = {
    trial:    { bg: '#ecfdf5', border: '#06b6d4', text: '#0e7490', label: '3-Day Trial' },
    '1month': { bg: '#dbeafe', border: '#2563eb', text: '#1e40af', label: '1 Month' },
    '3month': { bg: '#ede9fe', border: '#7c3aed', text: '#5b21b6', label: '3 Months' },
    '6month': { bg: '#fef3c7', border: '#d97706', text: '#92400e', label: '6 Months' },
};
const getPlanStyle = (planName, durationDays) => {
    const n = (planName || '').toLowerCase();
    if (n.includes('trial') || n.includes('3 day')) return PLAN_STYLES.trial;
    if (durationDays) {
        if (durationDays <= 7) return PLAN_STYLES.trial;
        if (durationDays <= 45) return PLAN_STYLES['1month'];
        if (durationDays <= 120) return PLAN_STYLES['3month'];
        return PLAN_STYLES['6month'];
    }
    if (n.includes('6 month')) return PLAN_STYLES['6month'];
    if (n.includes('3 month')) return PLAN_STYLES['3month'];
    return PLAN_STYLES['1month'];
};

const STATUS_COLORS = {
    active:   { bg: '#d1fae5', text: '#065f46' },
    expired:  { bg: '#fee2e2', text: '#991b1b' },
    paused:   { bg: '#fef3c7', text: '#92400e' },
    canceled: { bg: '#f3f4f6', text: '#6b7280' },
};

const PROG_DEFS = [
    { key: 'likes',          label: 'Likes',          color: '#1d4ed8', todayKey: 'likes_delivered' },
    { key: 'followers',      label: 'Followers',      color: '#2563eb', todayKey: 'followers_delivered' },
    { key: 'story_views',   label: 'Story Views',    color: '#0891b2', todayKey: 'story_views_delivered' },
    { key: 'comments',       label: 'Comments',       color: '#059669', todayKey: 'comments_delivered' },
    { key: 'reel_views',    label: 'Reel Views',     color: '#2563eb', todayKey: 'reel_views_delivered' },
    { key: 'profile_visits', label: 'Profile Visits', color: '#7c3aed', todayKey: 'profile_visits_delivered' },
    { key: 'saves',          label: 'Saves',          color: '#d97706', todayKey: 'saves_delivered' },
    { key: 'reposts',        label: 'Reposts',        color: '#dc2626', todayKey: 'reposts_delivered' },
];

const s = {
    h1:      { fontSize: 22, fontWeight: 700, marginBottom: 20 },
    filter:  { display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' },
    sel:     { padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, background: '#fff' },
    inp:     { padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, width: 180 },
    card:    { background: '#fff', borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,.06)', marginBottom: 14, overflow: 'hidden' },
    cardHead:{ padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #f3f4f6', cursor: 'pointer' },
    cardBody:{ padding: '16px 20px' },
    badge:   (st) => ({ display: 'inline-block', padding: '3px 10px', borderRadius: 100, fontSize: 11, fontWeight: 700, background: STATUS_COLORS[st]?.bg || '#f3f4f6', color: STATUS_COLORS[st]?.text || '#374151' }),
    planBadge: (p) => ({ display: 'inline-block', padding: '3px 12px', borderRadius: 100, fontSize: 12, fontWeight: 700, background: p?.bg || '#f3f4f6', color: p?.text || '#374151', border: `1px solid ${p?.border || '#d1d5db'}` }),
    section: { fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 1, marginTop: 18, marginBottom: 8 },
    totalRow:{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 8, marginTop: 10 },
    totalItem:{ background: '#f9fafb', borderRadius: 8, padding: '10px 12px', textAlign: 'center' },
    totalNum: { fontSize: 20, fontWeight: 800, color: '#1a1d23', display: 'block' },
    totalLbl: { fontSize: 10, color: '#9ca3af', marginTop: 2 },
    weekGrid:{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, marginTop: 14 },
    progRow: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 },
    progLbl: { fontSize: 13, color: '#374151', minWidth: 110 },
    table:   { width: '100%', borderCollapse: 'collapse', fontSize: 12, marginTop: 10 },
    th:      { padding: '8px 10px', background: '#f9fafb', textAlign: 'left', color: '#9ca3af', fontSize: 11, borderBottom: '1px solid #f3f4f6' },
    td:      { padding: '8px 10px', borderBottom: '1px solid #f9fafb', textAlign: 'left' },
};

function localDateStr(d) {
    const dt = d instanceof Date ? d : new Date(d);
    return `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,'0')}-${String(dt.getDate()).padStart(2,'0')}`;
}

function weekDays(mondayDate) {
    const days = [];
    const labels = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
    for (let i = 0; i < 7; i++) {
        const d = new Date(mondayDate);
        d.setDate(d.getDate() + i);
        days.push({ label: labels[i], date: localDateStr(d), dateObj: d });
    }
    return days;
}

function getMonday(d) {
    const dt = new Date(d);
    const day = dt.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    dt.setDate(dt.getDate() + diff);
    dt.setHours(0,0,0,0);
    return dt;
}

const fmtDT = (d) => new Date(d).toLocaleString('en-GB', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' });
const fmtShort = (dateStr) => {
    const d = new Date(dateStr);
    return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}`;
};

// ─── Detail panel (delivery + week calendar + totals + daily log) ─────────
function DetailPanel({ sub, detail }) {
    const todayDate = localDateStr(new Date());
    const [weekOffset, setWeekOffset] = useState(0);
    const [selectedDay, setSelectedDay] = useState(todayDate);

    const logByDate = {};
    (detail?.daily_logs || []).forEach(l => {
        const key = l.log_date ? localDateStr(new Date(l.log_date)) : null;
        if (key) logByDate[key] = l;
    });

    const selectedLog = logByDate[selectedDay] || null;
    const isToday = selectedDay === todayDate;
    const displayLog = selectedLog || (isToday ? (sub.today || null) : null);

    const deliveries = PROG_DEFS.map(d => ({
        ...d,
        delivered: displayLog?.[d.todayKey] || 0,
    }));
    const hasAny = deliveries.some(d => d.delivered > 0);

    const subStart = new Date(sub.start_date);
    const subEnd = new Date(sub.end_date);
    const effectiveEnd = subEnd < new Date() ? subEnd : new Date();
    const startMonday = getMonday(subStart);
    const currentMonday = getMonday(effectiveEnd);

    const displayMonday = new Date(currentMonday);
    displayMonday.setDate(displayMonday.getDate() + weekOffset * 7);

    const canPrev = displayMonday > startMonday;
    const canNext = weekOffset < 0;

    const week = weekDays(displayMonday);

    return (
        <div>
            <div style={s.section}>
                {isToday ? "TODAY'S DELIVERY" : `DELIVERY \u2014 ${new Date(selectedDay).toLocaleDateString('en-GB')}`}
            </div>
            {deliveries.map(d => (
                <div key={d.key} style={s.progRow}>
                    <div style={s.progLbl}>{d.label}</div>
                    <div style={{ flex: 1 }}>
                        {d.delivered > 0 ? (
                            <span style={{ fontSize: 14, fontWeight: 700, color: d.color }}>{d.delivered}</span>
                        ) : (
                            <span style={{ fontSize: 13, color: '#d1d5db' }}>&mdash;</span>
                        )}
                    </div>
                </div>
            ))}
            {!hasAny && (
                <div style={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }}>
                    {isToday ? 'No deliveries yet today.' : 'No deliveries on this day.'}
                </div>
            )}

            {/* Week calendar */}
            {detail && (
                <>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 18, marginBottom: 6 }}>
                        <button onClick={() => canPrev && setWeekOffset(o => o - 1)} disabled={!canPrev}
                            style={{ background: 'none', border: 'none', cursor: canPrev ? 'pointer' : 'default', fontSize: 18, color: canPrev ? '#1d4ed8' : '#d1d5db', padding: '4px 8px' }}>&#9664;</button>
                        <span style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 1 }}>
                            {fmtShort(week[0].date)} &mdash; {fmtShort(week[6].date)}
                        </span>
                        <button onClick={() => canNext && setWeekOffset(o => o + 1)} disabled={!canNext}
                            style={{ background: 'none', border: 'none', cursor: canNext ? 'pointer' : 'default', fontSize: 18, color: canNext ? '#1d4ed8' : '#d1d5db', padding: '4px 8px' }}>&#9654;</button>
                    </div>
                    <div className="og-week-grid" style={s.weekGrid}>
                        {week.map(w => {
                            const log = logByDate[w.date];
                            const inRange = (w.date >= localDateStr(subStart) && w.date < localDateStr(subEnd)) || !!log;
                            const tot = log ? (
                                (log.likes_delivered || 0) + (log.followers_delivered || 0) +
                                (log.story_views_delivered || 0) + (log.comments_delivered || 0) +
                                (log.reel_views_delivered || 0) + (log.profile_visits_delivered || 0) +
                                (log.saves_delivered || 0) + (log.reposts_delivered || 0)
                            ) : 0;
                            const isSel = w.date === selectedDay;
                            return (
                                <div key={w.date} onClick={() => inRange && setSelectedDay(w.date)}
                                    style={{
                                        textAlign: 'center', padding: '6px 4px', borderRadius: 8,
                                        background: log ? 'rgba(29,78,216,0.12)' : '#f9fafb',
                                        border: `1px solid ${log ? 'rgba(29,78,216,0.2)' : '#f3f4f6'}`,
                                        cursor: inRange ? 'pointer' : 'default',
                                        opacity: inRange ? 1 : 0.35,
                                        outline: isSel ? '2px solid #1d4ed8' : 'none', outlineOffset: -1,
                                    }}>
                                    <span style={{ fontSize: 9, color: '#9ca3af', display: 'block' }}>{w.label}</span>
                                    <span style={{ fontSize: 10, color: '#9ca3af', display: 'block' }}>{fmtShort(w.date)}</span>
                                    <span style={{ fontSize: 13, fontWeight: log ? 700 : 400, color: log ? '#1d4ed8' : '#d1d5db', display: 'block' }}>{log ? tot : '\u2014'}</span>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}

            {/* Totals */}
            {detail?.totals && (
                <>
                    <div style={s.section}>TOTAL DELIVERED</div>
                    <div className="og-grid-5" style={s.totalRow}>
                        {[
                            { n: detail.totals.total_likes, l: 'Likes' },
                            { n: detail.totals.total_followers, l: 'Followers' },
                            { n: detail.totals.total_story_views, l: 'Stories' },
                            { n: detail.totals.total_comments, l: 'Comments' },
                            { n: detail.totals.total_reel_views, l: 'Reels' },
                            ...(detail.totals.total_profile_visits > 0 ? [{ n: detail.totals.total_profile_visits, l: 'Visits' }] : []),
                            ...(detail.totals.total_saves > 0 ? [{ n: detail.totals.total_saves, l: 'Saves' }] : []),
                            ...(detail.totals.total_reposts > 0 ? [{ n: detail.totals.total_reposts, l: 'Reposts' }] : []),
                        ].map(t => (
                            <div key={t.l} style={s.totalItem}>
                                <span style={s.totalNum}>{t.n}</span>
                                <span style={s.totalLbl}>{t.l}</span>
                            </div>
                        ))}
                    </div>
                </>
            )}

            {/* Daily log table */}
            {detail?.daily_logs?.length > 0 && (
                <>
                    <div style={s.section}>DAILY LOG</div>
                    <div className="og-table-wrap">
                    <table style={s.table}>
                        <thead>
                            <tr>
                                {['Date','Likes','Followers','Stories','Comments','Reels','Visits','Saves','Reposts'].map(h => (
                                    <th key={h} style={s.th}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {detail.daily_logs.map(l => (
                                <tr key={l.id}>
                                    <td style={s.td}>{new Date(l.log_date).toLocaleDateString('en-GB')}</td>
                                    <td style={s.td}>{l.likes_delivered}</td>
                                    <td style={s.td}>{l.followers_delivered}</td>
                                    <td style={s.td}>{l.story_views_delivered}</td>
                                    <td style={s.td}>{l.comments_delivered}</td>
                                    <td style={s.td}>{l.reel_views_delivered}</td>
                                    <td style={s.td}>{l.profile_visits_delivered || 0}</td>
                                    <td style={s.td}>{l.saves_delivered || 0}</td>
                                    <td style={s.td}>{l.reposts_delivered || 0}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    </div>
                </>
            )}
        </div>
    );
}

// ─── Time progress bar ────────────────────────────────────────────────────
function TimeProgressBar({ sub }) {
    const start = new Date(sub.start_date);
    const end = new Date(sub.end_date);
    const now = new Date();
    const day = 86400000;

    if (now < start) {
        const hoursUntil = Math.ceil((start - now) / 3600000);
        return <div style={{ fontSize: 13, fontWeight: 600, color: '#d97706', marginBottom: 12 }}>Starts in {hoursUntil}h</div>;
    }

    const totalDays = Math.max(1, Math.round((end - start) / day));
    const elapsedDays = Math.min(totalDays, Math.max(1, Math.floor((now - start) / day) + 1));
    const pct = Math.min(100, Math.round((elapsedDays / totalDays) * 100));
    const isExpired = now >= end;

    return (
        <div style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 3 }}>
                <span style={{ fontWeight: 700, color: isExpired ? '#dc2626' : '#1a1d23' }}>
                    {isExpired ? 'Expired' : `Day ${elapsedDays} of ${totalDays}`}
                </span>
                <span style={{ color: '#6b7280' }}>{pct}%</span>
            </div>
            <div style={{ background: '#e5e7eb', borderRadius: 6, height: 7, overflow: 'hidden' }}>
                <div style={{ width: `${pct}%`, height: '100%', borderRadius: 6, background: isExpired ? '#dc2626' : 'linear-gradient(90deg, #1d4ed8, #06b6d4)', transition: 'width .3s' }} />
            </div>
        </div>
    );
}

// ─── Subscription Card ────────────────────────────────────────────────────
function SubCard({ sub }) {
    const [expanded, setExpanded] = useState(false);
    const [detail, setDetail]     = useState(null);
    const [loading, setLoading]   = useState(false);

    const durDays = sub.start_date && sub.end_date ? Math.round((new Date(sub.end_date) - new Date(sub.start_date)) / 86400000) : null;
    const plan = getPlanStyle(sub.plan_name, durDays);

    const toggleExpand = async () => {
        if (!expanded && !detail) {
            setLoading(true);
            try {
                const r = await adminGetSubscription(sub.id);
                setDetail(r.data);
            } catch {}
            setLoading(false);
        }
        setExpanded(!expanded);
    };

    return (
        <div style={s.card}>
            <div style={s.cardHead} onClick={toggleExpand}>
                <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: '#6b7280' }}>#{sub.client_order_id || sub.id}</span>
                        <span style={s.planBadge(plan)}>{plan.label}</span>
                        <span style={s.badge(sub.status)}>{(sub.status || '').toUpperCase()}</span>
                        {sub.order_channel && (
                            <span style={{
                                display: 'inline-block', padding: '1px 6px', borderRadius: 4, fontSize: 9, fontWeight: 700,
                                background: sub.order_channel === 'api' ? '#fef3c7' : '#dbeafe',
                                color: sub.order_channel === 'api' ? '#92400e' : '#1d4ed8',
                            }}>
                                {sub.order_channel === 'api' ? 'API' : 'WEB'}
                            </span>
                        )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <span style={{ fontSize: 16, fontWeight: 700, color: '#1a1d23' }}>
                            <a href={`https://instagram.com/${sub.target_username}`} target="_blank" rel="noopener noreferrer"
                               style={{ color: '#1a1d23', textDecoration: 'none' }}
                               onClick={e => e.stopPropagation()}>
                                @{sub.target_username}
                            </a>
                        </span>
                        <span style={{ fontSize: 12, color: '#6b7280', background: '#f0f4ff', padding: '2px 8px', borderRadius: 6 }}>
                            Client: <strong>{sub.client_username || sub.client_id}</strong>
                        </span>
                    </div>
                    <div style={{ fontSize: 12, color: '#6b7280', marginTop: 3 }}>
                        {fmtDT(sub.start_date)} &rarr; {fmtDT(new Date(new Date(sub.end_date).getTime() - 86400000))}
                        {sub.status === 'active' && sub.days_remaining > 0 && (
                            <span style={{ fontSize: 13, fontWeight: 600, color: '#1d4ed8', marginLeft: 8 }}>&middot; {sub.days_remaining} days left</span>
                        )}
                    </div>
                    <div style={{ display: 'flex', gap: 16, marginTop: 4, fontSize: 12, color: '#6b7280' }}>
                        {sub.order_quantity > 0 && sub.displayed_follows != null && (
                            <span>Following: <strong style={{ color: sub.displayed_follows >= sub.order_quantity ? '#059669' : '#d97706' }}>{sub.displayed_follows}/{sub.order_quantity}</strong></span>
                        )}
                        {sub.order_price && <span>Paid: <strong style={{ color: '#1a1d23' }}>&euro;{parseFloat(sub.order_price).toFixed(2)}</strong></span>}
                    </div>
                </div>
                <div style={{ fontSize: 16, color: '#9ca3af', marginLeft: 12 }}>
                    {expanded ? '\u25B2' : '\u25BC'}
                </div>
            </div>

            <div style={s.cardBody}>
                <TimeProgressBar sub={sub} />

                {expanded && (
                    loading ? (
                        <p style={{ color: '#9ca3af', fontSize: 13 }}>Loading...</p>
                    ) : (
                        <DetailPanel sub={sub} detail={detail} />
                    )
                )}
            </div>
        </div>
    );
}

// ─── Main page ────────────────────────────────────────────────────────────
const SUB_STATUSES = ['all', 'active', 'expired', 'paused', 'canceled'];

export default function AdminSubscriptions() {
    const [subs, setSubs]     = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('all');
    const [search, setSearch] = useState('');

    // Client filter
    const [allClients, setAllClients] = useState([]);
    const [clientSearch, setClientSearch] = useState('');
    const [clientId, setClientId] = useState('');
    const [showDropdown, setShowDropdown] = useState(false);

    useEffect(() => {
        adminGetClients().then(r => setAllClients(r.data || [])).catch(() => {});
    }, []);

    const load = () => {
        setLoading(true);
        const params = {};
        if (statusFilter !== 'all') params.status = statusFilter;
        if (clientId) params.client_id = clientId;
        adminGetSubscriptions(params)
            .then(r => setSubs(Array.isArray(r.data) ? r.data : []))
            .catch(() => setSubs([]))
            .finally(() => setLoading(false));
    };

    useEffect(load, [statusFilter, clientId]); // eslint-disable-line

    const filteredClients = clientSearch.length > 0
        ? allClients.filter(c =>
            c.username?.toLowerCase().includes(clientSearch.toLowerCase()) ||
            c.email?.toLowerCase().includes(clientSearch.toLowerCase()) ||
            String(c.id) === clientSearch
        ).slice(0, 8)
        : [];

    const selectClient = (c) => { setClientId(String(c.id)); setClientSearch(c.username); setShowDropdown(false); };
    const clearClient = () => { setClientId(''); setClientSearch(''); setShowDropdown(false); };

    const sq = search.toLowerCase();
    const displayed = sq
        ? subs.filter(sub =>
            sub.target_username?.toLowerCase().includes(sq) ||
            sub.client_username?.toLowerCase().includes(sq) ||
            sub.plan_name?.toLowerCase().includes(sq) ||
            String(sub.client_order_id).includes(sq) ||
            String(sub.id).includes(sq))
        : subs;

    const counts = { active: 0, expired: 0, paused: 0, canceled: 0 };
    subs.forEach(sub => { if (counts[sub.status] !== undefined) counts[sub.status]++; });

    return (
        <div>
            <h1 style={s.h1}>Subscriptions</h1>

            {/* Stats strip */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
                {[
                    { label: 'Active',   count: counts.active,   color: '#065f46', bg: '#d1fae5', key: 'active' },
                    { label: 'Expired',  count: counts.expired,  color: '#991b1b', bg: '#fee2e2', key: 'expired' },
                    { label: 'Canceled', count: counts.canceled, color: '#6b7280', bg: '#f3f4f6', key: 'canceled' },
                    { label: 'Total',    count: subs.length,     color: '#1a1d23', bg: '#f0f4ff', key: 'all' },
                ].map(c => (
                    <div key={c.key}
                        onClick={() => setStatusFilter(c.key)}
                        style={{
                            background: c.bg, borderRadius: 10, padding: '10px 16px', cursor: 'pointer',
                            border: statusFilter === c.key ? `2px solid ${c.color}` : '2px solid transparent',
                        }}>
                        <div style={{ fontSize: 22, fontWeight: 800, color: c.color }}>{c.count}</div>
                        <div style={{ fontSize: 12, color: c.color }}>{c.label}</div>
                    </div>
                ))}
            </div>

            {/* Filters */}
            <div style={s.filter}>
                <select style={s.sel} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                    {SUB_STATUSES.map(st => <option key={st} value={st}>{st.charAt(0).toUpperCase() + st.slice(1)}</option>)}
                </select>

                <div style={{ position: 'relative' }}>
                    <input
                        style={s.inp}
                        type="text"
                        placeholder="Client name or ID..."
                        value={clientSearch}
                        onChange={e => { setClientSearch(e.target.value); setShowDropdown(true); if (!e.target.value) clearClient(); }}
                        onFocus={() => clientSearch && setShowDropdown(true)}
                        onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
                    />
                    {clientId && (
                        <span onClick={clearClient}
                            style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', color: '#9ca3af', fontSize: 14 }}>&times;</span>
                    )}
                    {showDropdown && filteredClients.length > 0 && (
                        <div style={{
                            position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
                            background: '#fff', border: '1px solid #d1d5db', borderRadius: 8,
                            boxShadow: '0 4px 12px rgba(0,0,0,.12)', maxHeight: 220, overflowY: 'auto', marginTop: 2
                        }}>
                            {filteredClients.map(c => (
                                <div key={c.id} onMouseDown={() => selectClient(c)}
                                    style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 12, borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between' }}
                                    onMouseEnter={e => e.currentTarget.style.background = '#f0f4ff'}
                                    onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                                    <span style={{ fontWeight: 600 }}>{c.username}</span>
                                    <span style={{ color: '#9ca3af', fontSize: 10 }}>#{c.id}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <input style={s.inp} type="text" placeholder="Search target, client, plan..."
                    value={search} onChange={e => setSearch(e.target.value)} />
                {search && <span style={{ fontSize: 12, color: '#6b7280' }}>{displayed.length} / {subs.length}</span>}
            </div>

            {/* Cards */}
            {loading ? (
                <p style={{ color: '#9ca3af', padding: 24 }}>Loading...</p>
            ) : displayed.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 48, color: '#9ca3af', background: '#fff', borderRadius: 12 }}>
                    No subscriptions found.
                </div>
            ) : (
                displayed.map(sub => <SubCard key={sub.id} sub={sub} />)
            )}
        </div>
    );
}
